type InstallChoice = 'accepted' | 'dismissed';

export type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: InstallChoice }>;
};

type InstallListener = () => void;

let deferred: InstallPromptEvent | null = null;
let installed = false;
let listening = false;
const listeners = new Set<InstallListener>();

function notify() {
  listeners.forEach((listener) => listener());
}

export function isIosDevice(nav: {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
}) {
  const ua = nav.userAgent || '';
  const iPhoneOrIpad = /iphone|ipad|ipod/i.test(ua);
  const iPadOs = nav.platform === 'MacIntel' && (nav.maxTouchPoints || 0) > 1;
  return iPhoneOrIpad || iPadOs;
}

export function isRunningAsInstalledApp(win: {
  matchMedia?: (query: string) => { matches: boolean };
  navigator?: unknown;
}) {
  const standaloneDisplay = win.matchMedia?.('(display-mode: standalone)')?.matches;
  const iosStandalone = (win.navigator as { standalone?: boolean } | undefined)?.standalone;
  return Boolean(standaloneDisplay || iosStandalone);
}

export function getInstallState() {
  return {
    promptEvent: installed ? null : deferred,
    installed,
  };
}

export function subscribeInstallPrompt(listener: InstallListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function captureInstallPrompt(event: Event) {
  if (typeof (event as InstallPromptEvent).prompt !== 'function') return;
  const promptEvent = event as InstallPromptEvent;
  promptEvent.preventDefault();
  if (installed) return;
  deferred = promptEvent;
  notify();
}

export function markAppInstalled() {
  deferred = null;
  installed = true;
  notify();
}

export async function runInstallPrompt(): Promise<InstallChoice | 'unavailable'> {
  const current = deferred;
  if (!current || installed) return 'unavailable';
  deferred = null;
  try {
    const pending = current.prompt();
    notify();
    await pending;
    const choice = await current.userChoice;
    if (choice.outcome === 'accepted') {
      installed = true;
      notify();
    }
    return choice.outcome;
  } catch {
    notify();
    return 'unavailable';
  }
}

export function listenForInstallPrompt(target: Pick<Window, 'addEventListener'>) {
  if (listening) return;
  listening = true;
  target.addEventListener('beforeinstallprompt', (event) => {
    captureInstallPrompt(event);
  });
  target.addEventListener('appinstalled', () => {
    markAppInstalled();
  });
}

export function resetInstallPromptState() {
  deferred = null;
  installed = false;
  listening = false;
  listeners.clear();
}
